import React, { useEffect, useState } from 'react';
import { debugLog } from '@/utils/debug';
import { MeasurementUnit } from '@/types/shapes';

interface GridLinesProps {
  canvasSize: { width: number, height: number };
  pixelsPerCm: number;
  pixelsPerMm: number;
  measurementUnit: MeasurementUnit;
  origin: { x: number, y: number };
  isEmbedded?: boolean;
  showUnits?: boolean;
  showPositiveQuadrant?: boolean;
}

const GridLines: React.FC<GridLinesProps> = ({ 
  canvasSize, 
  pixelsPerCm, 
  pixelsPerMm, 
  measurementUnit, 
  origin,
  isEmbedded = false,
  showUnits = true,
  showPositiveQuadrant = false
}) => {
  // Default to 'cm' if measurementUnit is undefined
  const unit = measurementUnit || 'cm';
  
  // Define the text style to prevent selection
  const textStyle: React.CSSProperties = {
    userSelect: 'none' as const,
    pointerEvents: 'none'
  };

  // Get the device pixel ratio for logging purposes only
  const [devicePixelRatio, setDevicePixelRatio] = useState<number>(window.devicePixelRatio || 1);
  
  useEffect(() => {
    const updatePixelRatio = () => {
      setDevicePixelRatio(window.devicePixelRatio || 1);
    };
    
    window.addEventListener('resize', updatePixelRatio);
    return () => {
      window.removeEventListener('resize', updatePixelRatio);
    };
  }, []);
  
  // Use the provided pixel values directly without any correction
  const pixelsPerUnit = pixelsPerCm;
  const pixelsPerSmallUnit = pixelsPerMm;
  
  useEffect(() => {
    debugLog('GridLines rendering');
  }, [unit, pixelsPerUnit, pixelsPerSmallUnit, devicePixelRatio, origin]);

  // Visual padding for positive-quadrant rendering so lines/labels are inset from canvas edges
  const paddingPx = showPositiveQuadrant ? 12 : 0;

  // When positive quadrant is enabled, we render using a padded origin inside the canvas for aesthetics
  const renderOrigin = showPositiveQuadrant
    ? { x: Math.max(paddingPx, origin.x + paddingPx), y: Math.min(canvasSize.height - paddingPx, origin.y - paddingPx) }
    : origin;

  // Convenience bounds respecting padding
  const minX = 0 + paddingPx;
  const maxX = canvasSize.width - paddingPx;
  const minY = 0 + paddingPx;
  const maxY = canvasSize.height - paddingPx;

  // Add more buffer to ensure all grid lines are rendered in fullscreen
  // Calculate the range of grid lines needed based on the origin
  const minHorizontalLine = showPositiveQuadrant
    ? Math.floor(-renderOrigin.y / pixelsPerUnit) - 20 // negative indices up to origin
    : Math.floor(-renderOrigin.y / pixelsPerUnit) - 20;
  const maxHorizontalLine = showPositiveQuadrant
    ? 0 // only lines at or above origin (positive Y values)
    : Math.ceil((canvasSize.height - renderOrigin.y) / pixelsPerUnit) + 20;
  const minVerticalLine = showPositiveQuadrant ? 0 : Math.floor(-renderOrigin.x / pixelsPerUnit) - 20;
  const maxVerticalLine = Math.ceil((canvasSize.width - renderOrigin.x) / pixelsPerUnit) + 20;

  const majorGridLines = [];
  const minorGridLines = [];
  const gridLabels = [];

  // Horizontal major and minor lines
  for (let i = minHorizontalLine; i <= maxHorizontalLine; i++) {
    const yPosition = renderOrigin.y + i * pixelsPerUnit;
    majorGridLines.push(
      <line 
        key={`h-major-${i}`} 
        x1={minX} 
        y1={yPosition} 
        x2={maxX} 
        y2={yPosition} 
        stroke="#555B6E" 
        strokeWidth={i === 0 ? "1" : "0.5"} 
        strokeOpacity={i === 0 ? "1" : "0.8"}
      />
    );
    if (i < maxHorizontalLine && !isEmbedded) {
      // Draw 9 minor lines between major lines (for cm: mm lines, for inches: 1/10th inch lines)
      // Skip minor lines in embedded view for cleaner appearance
      for (let j = 1; j < 10; j++) {
        const minorY = yPosition + j * pixelsPerSmallUnit;
        if (minorY >= minY && minorY <= maxY) {
          minorGridLines.push(
            <line 
              key={`h-minor-${i}-${j}`} 
              x1={minX} 
              y1={minorY} 
              x2={maxX} 
              y2={minorY} 
              stroke="#555B6E" 
              strokeWidth="0.2" 
              strokeOpacity="0.5"
            />
          );
        }
      }
    }
    if (yPosition >= minY && yPosition <= maxY) {
      // Invert the y-axis label value to follow standard Cartesian coordinates
      // In Cartesian coordinates, y increases upward, so we negate the value
      const yLabelValue = -i;
      
      // Only show labels for non-zero values or the origin (0,0)
      if (i !== 0 || Math.abs(renderOrigin.x - canvasSize.width / 2) > 50) {
        gridLabels.push(
          <text 
            key={`h-label-${i}`} 
            x={minX + 2} 
            y={yPosition - 2} 
            fontSize="8" 
            fill="#555B6E"
            style={textStyle}
          >
            {yLabelValue}{(isEmbedded || !showUnits) ? '' : ` ${unit}`}
          </text>
        );
      }
    }
  }

  // Vertical major and minor lines
  for (let i = minVerticalLine; i <= maxVerticalLine; i++) {
    const xPosition = renderOrigin.x + i * pixelsPerUnit;
    majorGridLines.push(
      <line 
        key={`v-major-${i}`} 
        x1={xPosition} 
        y1={minY} 
        x2={xPosition} 
        y2={maxY} 
        stroke="#555B6E" 
        strokeWidth={i === 0 ? "1" : "0.5"} 
        strokeOpacity={i === 0 ? "1" : "0.8"}
      />
    );
    if (i < maxVerticalLine && !isEmbedded) {
      // Draw 9 minor lines between major lines
      // Skip minor lines in embedded view for cleaner appearance
      for (let j = 1; j < 10; j++) {
        const minorX = xPosition + j * pixelsPerSmallUnit;
        if (minorX >= minX && minorX <= maxX) {
          minorGridLines.push(
            <line 
              key={`v-minor-${i}-${j}`} 
              x1={minorX} 
              y1={minY} 
              x2={minorX} 
              y2={maxY} 
              stroke="#555B6E" 
              strokeWidth="0.2" 
              strokeOpacity="0.5"
            />
          );
        }
      }
    }
    if (xPosition >= minX && xPosition <= maxX) {
      // Only show labels for non-zero values or the origin (0,0)
      if (i !== 0 || Math.abs(renderOrigin.y - canvasSize.height / 2) > 50) {
        gridLabels.push(
          <text 
            key={`v-label-${i}`} 
            x={xPosition + 2} 
            y={minY + 10} 
            fontSize="8" 
            fill="#555B6E"
            style={textStyle}
          >
            {i}{(isEmbedded || !showUnits) ? '' : ` ${unit}`}
          </text>
        );
      }
    }
  }
  
  // Add a special label for the origin (0,0) if it's visible
  if (
    origin.x >= 0 && 
    origin.x <= canvasSize.width && 
    origin.y >= 0 && 
    origin.y <= canvasSize.height
  ) {
    // We've moved the origin label to the OriginIndicator component
    // So we don't need to add it here anymore
    // This prevents duplicate labels
  }

  // Add arrow markers for axes
  const axisArrows = [];
  
  // X-axis arrow (positive direction, right)
  const xAxisY = renderOrigin.y;
  if (xAxisY >= minY && xAxisY <= maxY) {
    const arrowX = maxX - 10; // 10px from right padded edge
    axisArrows.push(
      <g key="x-axis-arrow">
        <defs>
          <marker
            id="x-arrow"
            markerWidth="14"
            markerHeight="10"
            refX="13"
            refY="5"
            orient="auto"
          >
            <polygon
              points="0 0, 14 5, 0 10"
              fill="#555B6E"
            />
          </marker>
        </defs>
        <line
          x1={Math.max(minX + 10, arrowX - 20)}
          y1={xAxisY}
          x2={arrowX}
          y2={xAxisY}
          stroke="#555B6E"
          strokeWidth="1"
          markerEnd="url(#x-arrow)"
        />
      </g>
    );
  }
  
  // Y-axis arrow (positive direction, up)
  const yAxisX = renderOrigin.x;
  if (yAxisX >= minX && yAxisX <= maxX) {
    const arrowY = minY + 10; // 10px from top padded edge
    axisArrows.push(
      <g key="y-axis-arrow">
        <defs>
          <marker
            id="y-arrow"
            markerWidth="10"
            markerHeight="14"
            refX="7"
            refY="5"
            orient="-90"
          >
            <polygon
              points="0 0, 14 5, 0 10"
              fill="#555B6E"
            />
          </marker>
        </defs>
        <line
          x1={yAxisX}
          y1={arrowY + 20}
          x2={yAxisX}
          y2={arrowY}
          stroke="#555B6E"
          strokeWidth="1"
          markerEnd="url(#y-arrow)"
        />
      </g>
    );
  }

  return (
    <>
      {minorGridLines}
      {majorGridLines}
      {gridLabels}
      {axisArrows}
    </>
  );
};

export default GridLines; 